import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ServicesPage from '../Thesis_work/Services/ServicesPage';
import Book from '../Thesis_work/Book';
import { FaPython, FaJava } from "react-icons/fa";
import { SiGooglecolab, SiAnsys } from "react-icons/si";
import { RiBarChartBoxLine } from "react-icons/ri";


const tabs = ['Services', 'Thesis Work', 'Tools'];
const SWITCH_INTERVAL = 30000;

const ResearchHubPage = () => {

  const [activeTab, setActiveTab] = useState('Services');
  const [isAutoSwitching, setIsAutoSwitching] = useState(true);
  const [shouldNavigateToTools, setShouldNavigateToTools] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [autoSwitchTimeout, setAutoSwitchTimeout] = useState(null);
  const [userInteracting, setUserInteracting] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  
  const containerRef = useRef(null);


  // Sample tools data - in a real app, this would come from props or API
  const toolsData = [
    {
      id: 2,
      title: 'Plagiarism Checker',
      description: 'Detect and highlight duplicate or unoriginal content in your work.',
      icon: '🔍',
      category: 'Writing',
    },
    {
      id: 3,
      title: 'Data Visualizer',
      description: 'Transform raw data into interactive charts and insightful visual reports.',
      icon: '📊',
      category: 'Analysis',
    },
    {
      id: 5,
      title: 'Statistical Analysis',
      description: 'Run advanced statistical tests and calculations with automated accuracy.',
      icon: '🧮',
      category: 'Analysis',
    },
    {
      id: 6,
      title: 'Google Colab',
      description: 'Cloud-based Jupyter notebook environment for coding, collaboration, and research.',
      icon: <SiGooglecolab className="text-yellow-500 w-6 h-6" />,
      category: 'Analysis',
    },
    {
      id: 7,
      title: 'MATLAB',
      description: 'Powerful platform for numerical computing, data visualization, and simulations.',
      icon: '🔢',
      category: 'Implementation',
    },
    {
      id: 8,
      title: 'Python',
      description: 'Versatile programming language for AI, data science, and web development.',
      icon: <FaPython className="text-blue-500" />,
      category: 'Implementation',
    },
    {
      id: 9,
      title: 'Java',
      description: 'Robust, object-oriented programming language for scalable applications.',
      icon: <FaJava className="text-red-600 w-6 h-6" />,
      category: 'Implementation',
    },
    {
      id: 10,
      title: 'ANSYS',
      description: 'Industry-standard software for engineering design, simulation, and testing.',
      icon: <SiAnsys className="text-gray-700 w-6 h-6" />,
      category: 'Research',
    },
    {
      id: 11,
      title: 'Research Analytics',
      description: 'Comprehensive toolkit for analyzing datasets and generating research insights.',
      icon: <RiBarChartBoxLine className="text-indigo-500 w-6 h-6" />,
      category: 'Analysis',
    },
  ];

  // Handle user interaction detection
  const handleUserInteraction = (isInteracting) => {
    setUserInteracting(isInteracting);
    setLastActivity(Date.now());
    
    if (isInteracting) {
      // Clear any existing auto-switch timeout when user starts interacting
      if (autoSwitchTimeout) {
        clearTimeout(autoSwitchTimeout);
        setAutoSwitchTimeout(null);
      }
    }
  };

  // Monitor for user inactivity to resume auto-switching
  useEffect(() => {
    const checkInactivity = () => {
      const timeSinceLastActivity = Date.now() - lastActivity;
      const inactivityThreshold = 5000; // 5 seconds of inactivity
      
      if (timeSinceLastActivity > inactivityThreshold && userInteracting) {
        setUserInteracting(false);
        // Resume auto-switching after a brief delay
        const timeout = setTimeout(() => {
          setIsAutoSwitching(true);
        }, 2000);
        setAutoSwitchTimeout(timeout);
      }
    };

    const inactivityInterval = setInterval(checkInactivity, 1000);
    return () => clearInterval(inactivityInterval);
  }, [lastActivity, userInteracting, autoSwitchTimeout]);

  // Handle navigation to Tools tab from CardData
  useEffect(() => {
    if (shouldNavigateToTools) {
      setActiveTab('Tools');
      setIsAutoSwitching(false);
      setShouldNavigateToTools(false);
      
      // Clear any existing timeout
      if (autoSwitchTimeout) {
        clearTimeout(autoSwitchTimeout);
      }
      
      // Resume auto-switching after 10 seconds
      const timeout = setTimeout(() => {
        if (!userInteracting) {
          setIsAutoSwitching(true);
        }
      }, 10000);
      
      setAutoSwitchTimeout(timeout);
    }
  }, [shouldNavigateToTools, userInteracting, autoSwitchTimeout]);


  // Auto-switching functionality - now considers user interaction
  useEffect(() => {
    if (!isAutoSwitching || userInteracting) return;

    const interval = setInterval(() => {
      // Double-check user interaction before switching
      if (!userInteracting) {
        setActiveTab(prevTab => {
          const currentIndex = tabs.indexOf(prevTab);
          const nextIndex = (currentIndex + 1) % tabs.length;
          return tabs[nextIndex];
        });
      }
    }, SWITCH_INTERVAL);

    return () => clearInterval(interval);
  }, [isAutoSwitching, userInteracting]);


  // Handle manual tab click (pause auto-switching temporarily)
  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    setIsAutoSwitching(false);
    setUserInteracting(true);
    setLastActivity(Date.now());
    
    // Clear any existing timeout
    if (autoSwitchTimeout) {
      clearTimeout(autoSwitchTimeout);
    }
    
    // Resume auto-switching after 30 seconds of inactivity
    const timeout = setTimeout(() => {
      if (!userInteracting) {
        setIsAutoSwitching(true);
      }
    }, 30000);
    
    setAutoSwitchTimeout(timeout);
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    handleUserInteraction(true);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSwitchTimeout) {
        clearTimeout(autoSwitchTimeout);
      }
    };
  }, [autoSwitchTimeout]);

  // Filter tools based on search term
  const filteredTools = toolsData.filter(tool => 
    tool.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tool.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (

    <div 
      className='min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 overflow-x-hidden'
      ref={containerRef}
      onMouseMove={() => handleUserInteraction(true)}
      onMouseLeave={() => handleUserInteraction(false)}
      onFocus={() => handleUserInteraction(true)}
      onBlur={() => handleUserInteraction(false)}
    >
      {/* Main Content Section */}
      <div className='px-4 sm:px-6 lg:px-8 pb-8 sm:pt-0'>
        <div className='max-w-4xl sm:w-full lg:w-screen mx-auto'>
          
          {/* Auto-switch Status Indicator */}
          <div className="mb-4 flex justify-end">
            <div className="flex items-center space-x-2 text-xs text-gray-500 bg-white px-3 py-1 rounded-full shadow-sm">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                isAutoSwitching && !userInteracting ? 'bg-green-500 animate-pulse' : 'bg-gray-300'
              }`}></div>
              <span className="truncate max-w-[140px] sm:max-w-none">
                {isAutoSwitching && !userInteracting ? 'Auto-switching active' : 
                 userInteracting ? 'Auto-switch paused (user active)' : 'Auto-switch paused'}
              </span>
            </div>
          </div>
          
          {/* Tabs Content Card - Full width with responsive overflow */}
          <div 
            className='bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden w-full'
            onMouseEnter={() => handleUserInteraction(true)}
            onMouseLeave={() => handleUserInteraction(false)}
          >
            
            {/* Tab Navigation - Scrollable on mobile */}
            <div className='border-b border-gray-200 bg-gray-50 overflow-x-auto'>
              <div className='flex justify-center min-w-max sm:min-w-0'>
                <div className='flex'>
                  
                  <button
                    className={`px-4 sm:px-6 py-3 sm:py-4 font-semibold text-sm sm:text-base flex items-center transition-all duration-200 border-b-3 relative whitespace-nowrap ${
                      activeTab === 'Services'
                        ? 'text-blue-600 border-blue-600 bg-white shadow-sm'
                        : 'text-gray-600 border-transparent hover:text-blue-500 hover:bg-white hover:border-gray-300'
                    }`}
                    onClick={() => handleTabClick('Services')}
                    onMouseEnter={() => handleUserInteraction(true)}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path
                        fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                    Services
                    {activeTab === 'Services' && isAutoSwitching && !userInteracting && (
                      <div className='absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
                    )}
                  </button>
                  
                  <button
                    className={`px-4 sm:px-6 py-3 sm:py-4 font-semibold text-sm sm:text-base flex items-center transition-all duration-200 border-b-3 relative whitespace-nowrap ${
                      activeTab === 'Thesis Work'
                        ? 'text-blue-600 border-blue-600 bg-white shadow-sm'
                        : 'text-gray-600 border-transparent hover:text-blue-500 hover:bg-white hover:border-gray-300'
                    }`}
                    onClick={() => handleTabClick('Thesis Work')}
                    onMouseEnter={() => handleUserInteraction(true)}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path d='M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V4.804z' />
                    </svg>
                    Thesis Work
                    {activeTab === 'Thesis Work' && isAutoSwitching && !userInteracting && (
                      <div className='absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
                    )}
                  </button>
                  
                  <button
                    className={`px-4 sm:px-6 py-3 sm:py-4 font-semibold text-sm sm:text-base flex items-center transition-all duration-200 border-b-3 relative whitespace-nowrap ${
                      activeTab === 'Tools'
                        ? 'text-blue-600 border-blue-600 bg-white shadow-sm'
                        : 'text-gray-600 border-transparent hover:text-blue-500 hover:bg-white hover:border-gray-300'
                    }`}
                    onClick={() => handleTabClick('Tools')}
                    onMouseEnter={() => handleUserInteraction(true)}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path
                        fillRule='evenodd'
                        d='M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z'
                        clipRule='evenodd'
                      />
                    </svg>
                    Tools
                    {activeTab === 'Tools' && isAutoSwitching && !userInteracting && (
                      <div className='absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
                    )}
                  </button>
                  
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div 
              className='p-3 sm:p-6 lg:p-8 min-h-[400px]'
              onMouseEnter={() => handleUserInteraction(true)}
              onFocus={() => handleUserInteraction(true)}
            >
              
              {activeTab === 'Services' && (
                <div 
                  className='animate-fadeIn'
                  onMouseEnter={() => handleUserInteraction(true)}
                  onFocus={() => handleUserInteraction(true)}
                >
                  <div className='mb-6'>
                    <h2 className='text-xl sm:text-2xl font-bold text-gray-900 mb-2'>Our Services</h2>
                    <p className='text-gray-600'>Explore our comprehensive range of research services</p>
                  </div>
                  <div 
                    onMouseEnter={() => handleUserInteraction(true)}
                    onMouseMove={() => setLastActivity(Date.now())}
                  >
                    <ServicesPage />
                  </div>
                </div>
              )}

              {activeTab === 'Thesis Work' && (
                <div 
                  className='animate-fadeIn'
                  onMouseEnter={() => handleUserInteraction(true)}
                  onFocus={() => handleUserInteraction(true)}
                >
                  <div className='mb-6'>
                    <h2 className='text-xl sm:text-2xl font-bold text-gray-900 mb-2'>Thesis Work</h2>
                    <p className='text-gray-600'>Expert guidance and support for your thesis journey</p>
                  </div>
                  <div 
                    onMouseEnter={() => handleUserInteraction(true)}
                    onMouseMove={() => setLastActivity(Date.now())}
                  >
                    <Book onComplete={() => setShouldNavigateToTools(false)} />
                  </div>
                </div>
              )}

              {activeTab === 'Tools' && (
                <div 
                  className='animate-fadeIn'
                  onMouseEnter={() => handleUserInteraction(true)}
                  onFocus={() => handleUserInteraction(true)}
                >
                  <div className='mb-6'>
                    <h2 className='text-xl sm:text-2xl font-bold text-gray-900 mb-2'>Research Tools</h2>
                    <p className='text-gray-600 mb-4'>Access powerful tools to enhance your research workflow</p>
                    
                    {/* Search and Filter Section */}
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          placeholder="Search tools..."
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                          value={searchTerm}
                          onChange={handleSearchChange}
                          onFocus={() => handleUserInteraction(true)}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Tools Grid */}
                  <div 
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
                    onMouseEnter={() => handleUserInteraction(true)}
                  >
                    {filteredTools.map(tool => (
                      <div 
                        key={tool.id} 
                        className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 hover:shadow-md transition-shadow"
                        onMouseEnter={() => handleUserInteraction(true)}
                        onMouseMove={() => setLastActivity(Date.now())}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <span className="text-2xl sm:text-3xl">{tool.icon}</span>
                        </div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{tool.title}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm mb-4">{tool.description}</p>
                        <div className="flex justify-between items-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {tool.category}
                          </span>
                          <Link 
                            to="/thesis-home" 
                            className="text-xs sm:text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center"
                          >
                            Use Tool
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth="2" 
                                d="M14 5l7 7m0 0l-7 7m7-7H3">
                              </path>
                            </svg>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .border-b-3 {
          border-bottom-width: 3px;
        }
      `}</style>
    </div>
  );
};

export default ResearchHubPage;